import { useState, useEffect, useRef, useCallback } from 'react';
import type { AgentInfo } from '../../utils/agents';

// ── Conversation data ──────────────────────────────────────────────────────

const CONVERSATION_PAIRS: Array<[string, string][]> = [
  // Funny personal
  [
    ["Did you see the new coffee machine?", "It makes oat milk lattes now!"],
    ["My cat walked across my keyboard again", "Did it write better code than usual?"],
    ["I've been on hold with IT for 20 min", "Have you tried turning it off and on again?"],
    ["Anyone else's Slack have 847 unread?", "Those are rookie numbers"],
    ["I dreamed about merge conflicts", "That's called a nightmare"],
    ["Is it lunch yet?", "It's 9:30 AM..."],
    ["My standup update: I stood up", "Technically correct. The best kind."],
    ["Who ate my labeled lunch?", "The fridge is a lawless wasteland"],
    ["I accidentally replied-all again", "We've all been there... repeatedly"],
    ["The WiFi is being dramatic today", "It's going through a phase"],
  ],
  // Work related
  [
    ["The Shopify sync finished", "Any errors this time?"],
    ["Need to review the email campaign", "I'll take a look after lunch"],
    ["The flash report looks good today", "Revenue is trending up!"],
    ["Etsy listings need updating", "I'll prioritize those this afternoon"],
    ["New product photos are ready", "Let me add them to the content pipeline"],
    ["The Meta ads ROAS improved!", "Which campaign drove the lift?"],
    ["Customer feedback is really positive", "The new veil collection is a hit"],
    ["Weekly analytics are compiled", "I'll present them in the standup"],
    ["Pinterest traffic spiked yesterday", "The wedding season content is working"],
    ["Budget check passed this morning", "We're well within limits"],
  ],
  // Mix of both
  [
    ["I automated my morning routine", "Even the coffee part?"],
    ["The Airtable sync took 0.3 seconds", "Speed demon! High five!"],
    ["Just deployed to production", "Hold my coffee... and my anxiety"],
    ["The data pipeline is *chef's kiss*", "Tell me more about this pipeline"],
    ["Who scheduled a meeting at 4:59 PM?", "Someone who enjoys chaos"],
    ["The dashboard looks amazing", "Dark mode makes everything better"],
    ["I found a bug", "Feature. You found a feature."],
    ["Customer just left a 5-star review!", "Screenshot it! Frame it! Celebrate!"],
    ["The sync scripts are all green", "Music to my ears"],
    ["Quarterly goals are on track", "Time to set even bigger ones!"],
  ],
];

// ── Zone layout ────────────────────────────────────────────────────────────

interface ZoneRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const ZONES: Record<string, ZoneRect> = {
  'C-Suite': { x: 5, y: 2, w: 90, h: 12 },
  cfo:       { x: 2, y: 18, w: 22, h: 78 },
  cmo:       { x: 26, y: 18, w: 24, h: 78 },
  coo:       { x: 52, y: 18, w: 24, h: 78 },
  cco:       { x: 78, y: 18, w: 20, h: 78 },
};

export const DEPT_TO_ZONE: Record<string, string> = {
  'C-Suite':        'C-Suite',
  '00-Admin':       'coo',
  '02-Products':    'coo',
  '10-Marketplaces':'coo',
  '13-Codebases':   'coo',
  '01-Brand':       'cmo',
  '03-Campaigns':   'cmo',
  '05-Email':       'cmo',
  '06-Socials':     'cmo',
  '07-Ads':         'cmo',
  '08-Website':     'cmo',
  '09-UGC':         'cmo',
  '11-Analytics':   'cfo',
  '12-Finance':     'cfo',
  '04-Content':     'cco',
};

export const ZONE_LABELS: Record<string, string> = {
  'C-Suite': 'C-Suite Corner',
  cfo:       'CFO Zone — Finance & Data',
  cmo:       'CMO Zone — Marketing & Brand',
  coo:       'COO Zone — Ops & Tech',
  cco:       'CCO Zone — Creative & Content',
};

export const ZONE_COLORS: Record<string, string> = {
  'C-Suite': 'bg-accent-purple/5',
  cfo:       'bg-avatar-cyan/5',
  cmo:       'bg-avatar-pink/5',
  coo:       'bg-avatar-blue/5',
  cco:       'bg-avatar-orange/5',
};

export const ZONE_BORDER_COLORS: Record<string, string> = {
  'C-Suite': 'border-accent-purple/20',
  cfo:       'border-avatar-cyan/20',
  cmo:       'border-avatar-pink/20',
  coo:       'border-avatar-blue/20',
  cco:       'border-avatar-orange/20',
};

// ── Position computation ───────────────────────────────────────────────────

export interface AgentPosition {
  x: number; // percentage
  y: number; // percentage
  zone: string;
}

export function computeAgentPositions(agents: AgentInfo[]): Map<string, AgentPosition> {
  const positions = new Map<string, AgentPosition>();

  // Group agents by zone
  const zoneAgents: Record<string, AgentInfo[]> = {};
  for (const agent of agents) {
    const zone = DEPT_TO_ZONE[agent.department] || 'coo';
    if (!zoneAgents[zone]) zoneAgents[zone] = [];
    zoneAgents[zone].push(agent);
  }

  // Position agents in a grid within each zone
  for (const [zoneName, zoneMembers] of Object.entries(zoneAgents)) {
    const rect = ZONES[zoneName];
    if (!rect) continue;

    const cols = zoneName === 'C-Suite' ? 4 : 3;
    const rows = Math.ceil(zoneMembers.length / cols);

    const padX = 3;
    const padY = 6;
    const cellW = (rect.w - padX * 2) / cols;
    const cellH = (rect.h - padY * 2) / Math.max(rows, 1);

    for (let i = 0; i < zoneMembers.length; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = rect.x + padX + col * cellW + cellW / 2;
      const y = rect.y + padY + row * cellH + cellH / 2;
      positions.set(zoneMembers[i].slug, { x, y, zone: zoneName });
    }
  }

  return positions;
}

// ── Conversation state machine ─────────────────────────────────────────────

export interface ConversationMessage {
  speaker: 'agent1' | 'agent2';
  text: string;
}

export interface Conversation {
  id: string;
  agent1Slug: string;
  agent2Slug: string;
  messages: ConversationMessage[];
  currentMessageIndex: number;
  phase: 'walking' | 'talking' | 'returning' | 'done';
  startedAt: number;
}

const MAX_CONCURRENT = 3;
const WALK_DURATION = 2000;
const MESSAGE_DURATION = 3000;
const RETURN_DURATION = 2000;
const MIN_INTERVAL = 8000;
const MAX_INTERVAL = 12000;

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickRandomConversation(): [string, string] {
  const category = CONVERSATION_PAIRS[randomInt(0, CONVERSATION_PAIRS.length - 1)];
  return category[randomInt(0, category.length - 1)];
}

let conversationCounter = 0;

export function useConversations(agents: AgentInfo[]) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const busyAgents = useRef(new Set<string>());
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phaseTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const positions = useRef<Map<string, AgentPosition>>(new Map());

  // Compute positions once
  useEffect(() => {
    if (agents.length > 0) {
      positions.current = computeAgentPositions(agents);
    }
  }, [agents]);

  const getAgentPosition = useCallback(
    (slug: string): { x: number; y: number } => {
      const pos = positions.current.get(slug);
      return pos ? { x: pos.x, y: pos.y } : { x: 50, y: 50 };
    },
    []
  );

  const startConversation = useCallback(() => {
    // Filter available agents (not currently busy)
    const available = agents.filter((a) => !busyAgents.current.has(a.slug));
    if (available.length < 2) return;

    // Check max concurrent
    const activeCount = busyAgents.current.size / 2; // each conversation uses 2 agents
    if (activeCount >= MAX_CONCURRENT) return;

    // Pick two random agents
    const idx1 = randomInt(0, available.length - 1);
    let idx2 = randomInt(0, available.length - 2);
    if (idx2 >= idx1) idx2++;

    const agent1 = available[idx1];
    const agent2 = available[idx2];
    const [line1, line2] = pickRandomConversation();

    const id = `conv-${++conversationCounter}`;
    const conversation: Conversation = {
      id,
      agent1Slug: agent1.slug,
      agent2Slug: agent2.slug,
      messages: [
        { speaker: 'agent1', text: line1 },
        { speaker: 'agent2', text: line2 },
      ],
      currentMessageIndex: -1,
      phase: 'walking',
      startedAt: Date.now(),
    };

    busyAgents.current.add(agent1.slug);
    busyAgents.current.add(agent2.slug);

    setConversations((prev) => [...prev, conversation]);

    // Phase: walking -> talking
    const walkTimer = setTimeout(() => {
      setConversations((prev) =>
        prev.map((c) =>
          c.id === id ? { ...c, phase: 'talking', currentMessageIndex: 0 } : c
        )
      );

      // Show message 1, then message 2
      const msg2Timer = setTimeout(() => {
        setConversations((prev) =>
          prev.map((c) =>
            c.id === id ? { ...c, currentMessageIndex: 1 } : c
          )
        );

        // Phase: talking -> returning
        const returnTimer = setTimeout(() => {
          setConversations((prev) =>
            prev.map((c) =>
              c.id === id ? { ...c, phase: 'returning', currentMessageIndex: -1 } : c
            )
          );

          // Phase: returning -> done
          const doneTimer = setTimeout(() => {
            busyAgents.current.delete(agent1.slug);
            busyAgents.current.delete(agent2.slug);
            setConversations((prev) => prev.filter((c) => c.id !== id));
          }, RETURN_DURATION);
          phaseTimers.current.add(doneTimer);
        }, MESSAGE_DURATION);
        phaseTimers.current.add(returnTimer);
      }, MESSAGE_DURATION);
      phaseTimers.current.add(msg2Timer);
    }, WALK_DURATION);
    phaseTimers.current.add(walkTimer);
  }, [agents]);

  // Start the interval
  useEffect(() => {
    if (agents.length < 2) return;

    const scheduleNext = () => {
      const delay = randomInt(MIN_INTERVAL, MAX_INTERVAL);
      intervalRef.current = setTimeout(() => {
        startConversation();
        scheduleNext();
      }, delay);
    };

    // First conversation after a short delay
    const initialTimer = setTimeout(() => {
      startConversation();
      scheduleNext();
    }, 2000);

    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
      clearTimeout(initialTimer);
      phaseTimers.current.forEach(clearTimeout);
      phaseTimers.current.clear();
      busyAgents.current.clear();
    };
  }, [agents, startConversation]);

  const triggerExtraConversation = useCallback(() => {
    // Trigger 2-3 extra conversations for "Water Cooler Break"
    startConversation();
    setTimeout(() => startConversation(), 500);
    setTimeout(() => startConversation(), 1200);
  }, [startConversation]);

  return {
    conversations,
    getAgentPosition,
    triggerExtraConversation,
    positions: positions.current,
  };
}
